import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { Auth0Application } from '../../../../model/auth0-application';
// See C001 - 2026-04-17 - HE
// import { Auth0ApplicationService } from '../../../../services.module';
import { Auth0ApplicationService } from '../../../../services/auth0-application.service';
import { Subject, takeUntil, finalize, timeout, catchError, of } from 'rxjs';
import { MatSlideToggleModule } from "@angular/material/slide-toggle";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatInputModule } from "@angular/material/input";
import { MatIconModule } from "@angular/material/icon";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatButtonModule } from "@angular/material/button";
import { MatTooltipModule } from "@angular/material/tooltip";
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-ipaddress-origin',
  templateUrl: './ipaddress-origin.component.html',
  styleUrls: ['./ipaddress-origin.component.scss'],
  imports: [CommonModule, ReactiveFormsModule, MatSlideToggleModule, MatProgressSpinnerModule, MatInputModule, MatIconModule, MatFormFieldModule, MatButtonModule, MatTooltipModule],
  standalone: true
})
export class IpaddressOriginComponent implements OnInit, OnDestroy {
  @Input() application!: Auth0Application;

  ipAddressForm: FormGroup;
  isAdding = false;
  isLoading = false;
  errorMessage = '';
  successMessage = '';

  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private auth0Service: Auth0ApplicationService
  ) {
    this.ipAddressForm = this.fb.group({
      ipAddress: ['', [Validators.required, Validators.pattern(/^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/)]]
    });
  }

  ngOnInit(): void {
    if (!this.application) {
      this.errorMessage = 'Application data is required';
    }
  }

  ngOnDestroy(): void {
    // Reset any loading states before destroying
    this.isLoading = false;
    this.isAdding = false;
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggleIPAddressValidation(): void {
    if (!this.application?.clientId) return;

    this.isLoading = true;
    this.errorMessage = '';
    this.successMessage = '';

    this.auth0Service.toggleIPAddressOrigin(this.application.clientId)
      .pipe(
        takeUntil(this.destroy$),
        timeout(30000), // 30 second timeout
        catchError((error: any) => {
          this.errorMessage = 'Failed to toggle IP address validation';
          console.error('Error toggling IP address validation:', error);
          return of(null); // Return empty observable to prevent error
        }),
        finalize(() => {
          this.isLoading = false; // Always reset loading state
        })
      )
      .subscribe({
        next: (updatedApp: Auth0Application | null) => {
          if (updatedApp) {
            this.application = updatedApp;
            this.successMessage = `IP address validation ${updatedApp.validateIPAddressOrigin ? 'enabled' : 'disabled'} successfully`;
          }
        }
      });
  }

  addIPAddress(): void {
    if (this.ipAddressForm.invalid || !this.application?.clientId) return;

    const ipAddress = this.ipAddressForm.get('ipAddress')?.value;
    if (!ipAddress) return;

    this.isAdding = true;
    this.errorMessage = '';
    this.successMessage = '';

    this.auth0Service.addIPAddressOrigin(this.application.clientId, ipAddress)
      .pipe(
        takeUntil(this.destroy$),
        timeout(30000), // 30 second timeout
        catchError((error: any) => {
          this.errorMessage = `Failed to add IP address ${ipAddress}`;
          console.error('Error adding IP address:', error);
          return of(null);
        }),
        finalize(() => {
          this.isAdding = false; // Always reset loading state
        })
      )
      .subscribe({
                        next: (updatedApp: Auth0Application | null) => {
          if (updatedApp) {
            // Store the current IP addresses list
            const currentIPAddresses = this.application?.ipAddressOrigins || [];

            // Update only specific properties from the response, preserve IP addresses
            this.application = {
              ...this.application,
              ...updatedApp,
              ipAddressOrigins: [...currentIPAddresses, ipAddress]
            };

            this.ipAddressForm.reset();
            this.successMessage = `IP address ${ipAddress} added successfully`;
          }
        }
      });
  }

  removeIPAddress(ipAddress: string): void {
    if (!this.application?.clientId) return;

    this.isLoading = true;
    this.errorMessage = '';
    this.successMessage = '';

    console.log('Removing IP address:', ipAddress);
    console.log('Current IP addresses:', this.application?.ipAddressOrigins);

    this.auth0Service.removeIPAddressOrigin(this.application.clientId, ipAddress)
      .pipe(
        takeUntil(this.destroy$),
        timeout(30000), // 30 second timeout
        catchError((error: any) => {
          this.errorMessage = `Failed to remove IP address ${ipAddress}`;
          console.error('Error removing IP address:', error);
          return of(null);
        }),
        finalize(() => {
          this.isLoading = false; // Always reset loading state
        })
      )
      .subscribe({
        next: (updatedApp: Auth0Application | null) => {
          console.log('Remove API response:', updatedApp);

          if (updatedApp) {
            // Store the current IP addresses list
            const currentIPAddresses = this.application?.ipAddressOrigins || [];
            console.log('Stored current IP addresses:', currentIPAddresses);

            // Update only specific properties from the response, preserve IP addresses
            this.application = {
              ...this.application,
              ...updatedApp,
              ipAddressOrigins: currentIPAddresses.filter(ip => ip !== ipAddress)
            };
            console.log('Final IP addresses after removal:', this.application.ipAddressOrigins);

            this.successMessage = `IP address ${ipAddress} removed successfully`;
          }
        }
      });
  }

  clearMessages(): void {
    this.errorMessage = '';
    this.successMessage = '';
    // Also reset any stuck loading states
    this.isLoading = false;
    this.isAdding = false;
  }

  get ipAddresses(): string[] {
    return this.application?.ipAddressOrigins || [];
  }

  get hasIPAddresses(): boolean {
    return this.ipAddresses.length > 0;
  }

  trackByIpAddress(index: number, ipAddress: string): string {
    return ipAddress;
  }
}

// ========================================
// CHANGE LOG
// ========================================
// C001: Changed Auth0ApplicationService import from '../../../../services.module' to '../../../../services/auth0-application.service'. Previous behavior: services.module.ts imports the service for NgModule providers but does not export it as a public TypeScript symbol, so importing Auth0ApplicationService from the module file caused TS2459 and the Angular compiler treated the class as an invalid DI token (TS-992003). New behavior: Import the injectable class from its source file so the constructor receives a proper injection token. Reason: Fix portaladmin production build failures. Context: Auth0 lazy module and standalone IpaddressOriginComponent. Impact: No runtime behavior change; DI unchanged (providedIn: 'root'). - 2026-04-17 - HE
